import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articlesStore, localizeArticle } from "@/store/articlesStore";
import type { ManagedArticle } from "@/store/articlesStore";
import { useCategoryLocalize } from "@/hooks/useCategoryLocalize";
import { throttle } from "@/lib/throttle";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { getSectionFlags } from "@/store/siteSettingsStore";
import { formatArticleDate } from "@/lib/dateFormatter";

type SlideData = {
  id: number | string;
  category: string;
  categorySlug?: string;
  title: string;
  description: string;
  image: string;
  readTime: string;
  author: string;
  views: string;
  date: string;
  tag?: string;
  slug?: string;
  coverImageSettings?: ManagedArticle["coverImageSettings"];
};

const createFallbackSlides = (t: (key: string) => string): SlideData[] => [
  {
    id: "fallback-1",
    category: t("home.heroFallback.1.category"),
    title: t("home.heroFallback.1.title"),
    description: t("home.heroFallback.1.description"),
    image: "https://readdy.ai/api/search-image?query=luxury%20travel%20destination%20aerial%20view%20golden%20sunset%20over%20modern%20city%20skyline%20with%20turquoise%20water%20and%20palm%20trees%20vibrant%20colors%20cinematic%20photography%20high%20quality%20professional&width=1920&height=1080&seq=hero-fallback-1&orientation=landscape",
    readTime: t("common.minutes", { count: 3 }),
    author: t("common.editorialTeam"),
    views: "0",
    date: new Date().toISOString(),
    slug: "",
  },
  {
    id: "fallback-2",
    category: t("home.heroFallback.2.category"),
    title: t("home.heroFallback.2.title"),
    description: t("home.heroFallback.2.description"),
    image: "https://readdy.ai/api/search-image?query=beautiful%20gulf%20city%20skyline%20at%20night%20with%20reflections%20on%20water%20modern%20architecture%20illuminated%20buildings%20luxury%20hotels%20stunning%20panoramic%20view%20professional%20photography&width=1920&height=1080&seq=hero-fallback-2&orientation=landscape",
    readTime: t("common.minutes", { count: 5 }),
    author: t("common.editorialTeam"),
    views: "0",
    date: new Date().toISOString(),
    slug: "",
  },
  {
    id: "fallback-3",
    category: t("home.heroFallback.3.category"),
    title: t("home.heroFallback.3.title"),
    description: t("home.heroFallback.3.description"),
    image: "https://readdy.ai/api/search-image?query=luxury%20five%20star%20hotel%20resort%20pool%20area%20with%20palm%20trees%20and%20ocean%20view%20elegant%20architecture%20warm%20golden%20light%20high%20end%20travel%20destination%20professional%20photography&width=1920&height=1080&seq=hero-fallback-3&orientation=landscape",
    readTime: t("common.minutes", { count: 4 }),
    author: t("common.editorialTeam"),
    views: "0",
    date: new Date().toISOString(),
    slug: "",
  },
];

interface HeroSliderProps {
  bindingSlug?: string;
}

export default function HeroSlider({ bindingSlug }: HeroSliderProps) {
  const { t, i18n } = useTranslation();
  const localizeCat = useCategoryLocalize();
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const [slides, setSlides] = useState<SlideData[]>(() => createFallbackSlides(t));
  const [slidesLoaded, setSlidesLoaded] = useState(false);
  const [tickerItems, setTickerItems] = useState<{ title: string; slug: string }[]>([]);
  const [tickerIndex, setTickerIndex] = useState(0);
  const [tickerPhase, setTickerPhase] = useState<"in" | "show" | "out">("in");
  const { settings } = useSiteSettings();
  const flags = getSectionFlags(settings?.articleDisplayFlags, "heroSlider");

  const mapArticle = (a: ManagedArticle): SlideData => ({
    id: a.id,
    category: a.category,
    categorySlug: a.categorySlug,
    title: a.title,
    description: a.excerpt,
    image: a.image || "",
    readTime: a.readTime,
    author: a.author,
    views: a.views,
    date: a.date,
    tag: a.status === "featured" ? "featured" : undefined,
    slug: a.slug,
    coverImageSettings: a.coverImageSettings,
  });

  useEffect(() => {
    let cancelled = false;

    const loadSlides = async () => {
      try {
        setSlides(createFallbackSlides(t));
        // طلب واحد فقط — الكاش يتولى الباقي
        let heroArticles = await articlesStore.getHeroSlider();
        if (cancelled) return;

        // If bindingSlug is set (non-empty), filter hero articles by category
        if (bindingSlug && heroArticles.length > 0) {
          heroArticles = heroArticles.filter(
            (a) => a.categorySlug === bindingSlug || a.secondaryCategorySlug === bindingSlug
          );
        }

        if (heroArticles.length > 0) {
          setSlides(heroArticles.map((article) => localizeArticle(article, i18n.language)).map(mapArticle));
        } else {
          // Fallback: استخدم نفس مقالات featured المنشورة (بدون طلب جديد)
          let fallback = await articlesStore.getPublishedAndFeatured(5);
          if (cancelled) return;
          if (bindingSlug && fallback.length > 0) {
            fallback = fallback.filter(
              (a) => a.categorySlug === bindingSlug || a.secondaryCategorySlug === bindingSlug
            );
          }
          const withImages = fallback.filter((a) => a.image).slice(0, 5);
          if (withImages.length > 0) {
            setSlides(withImages.map((article) => localizeArticle(article, i18n.language)).map(mapArticle));
          }
        }
      } catch (_) {
        // keep FALLBACK_SLIDES on error
      } finally {
        if (!cancelled) setSlidesLoaded(true);
      }
    };

    // تشغيل الطلبين بالتوازي — لا نُشغّل getBreakingNews مرتين!
    const loadBreaking = articlesStore.getBreakingNews()
      .then((articles: ManagedArticle[]) => {
        if (!cancelled && articles.length > 0) {
          setTickerItems(articles.map((article) => localizeArticle(article, i18n.language)).map((a) => ({ title: a.title, slug: a.slug ?? "" })));
        }
      })
      .catch(() => {/* ignore */});

    Promise.all([loadSlides(), loadBreaking]);

    // ── Realtime auto-refresh (مع throttle لتجنب loop) ─────
    const handleArticlesChange = () => {
      articlesStore.reset();
      loadSlides();
    };
    const throttledHandleChange = throttle(handleArticlesChange, 3000);
    window.addEventListener("alsiyahiah:articles:updated", throttledHandleChange);

    return () => {
      cancelled = true;
      window.removeEventListener("alsiyahiah:articles:updated", throttledHandleChange);
    };
  }, [bindingSlug, i18n.language]);

  const totalSlides = slides.length;

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % totalSlides);
    setProgress(0);
  }, [totalSlides]);

  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + totalSlides) % totalSlides);
    setProgress(0);
  }, [totalSlides]);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          next();
          return 0;
        }
        return p + 0.4;
      });
    }, 30);
    return () => clearInterval(interval);
  }, [next]);

  const navigate = useNavigate();
  const slide = slides[current] ?? slides[0];

  const defaultItems = [
    { title: t("home.breakingFallback.1"), slug: "" },
    { title: t("home.breakingFallback.2"), slug: "" },
    { title: t("home.breakingFallback.3"), slug: "" },
    { title: t("home.breakingFallback.4"), slug: "" },
  ];
  const activeItems = tickerItems.length > 0 ? tickerItems : defaultItems;

  // Fade ticker cycle: in(600ms) → show(5s) → out(600ms) → next
  useEffect(() => {
    if (activeItems.length === 0) return;
    let timeout: ReturnType<typeof setTimeout>;
    if (tickerPhase === "in") {
      timeout = setTimeout(() => setTickerPhase("show"), 600);
    } else if (tickerPhase === "show") {
      timeout = setTimeout(() => setTickerPhase("out"), 5000);
    } else {
      timeout = setTimeout(() => {
        setTickerIndex((i) => (i + 1) % activeItems.length);
        setTickerPhase("in");
      }, 600);
    }
    return () => clearTimeout(timeout);
  }, [tickerPhase, activeItems.length]);

  // Always render — never return null
  // Show skeleton while loading (but slides are already set to FALLBACK_SLIDES)
  if (!slidesLoaded) {
    return (
      <section className="relative h-screen min-h-[600px] max-h-[900px] overflow-hidden bg-[#0A1628]">
        {/* Skeleton background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0d1f3c] to-[#0A1628] animate-pulse" />
        <div className="absolute inset-0 flex items-end pb-20 z-20 w-full">
          <div className="max-w-screen-xl mx-auto px-6 md:px-10 w-full">
            <div className="max-w-3xl space-y-4">
              <div className="h-5 w-24 bg-white/10 rounded-full animate-pulse" />
              <div className="h-12 w-3/4 bg-white/10 rounded-xl animate-pulse" />
              <div className="h-8 w-1/2 bg-white/10 rounded-xl animate-pulse" />
              <div className="h-5 w-2/3 bg-white/10 rounded-lg animate-pulse" />
              <div className="h-11 w-36 bg-white/10 rounded-full animate-pulse mt-2" />
            </div>
          </div>
        </div>
        {/* Ticker skeleton */}
        <div className="absolute bottom-0 right-0 left-0 z-20 bg-[#D4AF37]/95 h-10 flex items-center px-4 gap-3">
          <div className="h-5 w-12 bg-red-600 rounded" />
          <div className="h-4 w-64 bg-[#0A1628]/20 rounded animate-pulse" />
        </div>
      </section>
    );
  }

  if (!slide) return null;

  return (
    <section className="relative h-screen min-h-[600px] max-h-[900px] overflow-hidden">
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 z-30 h-0.5 bg-white/20">
        <div
          className="h-full bg-[#D4AF37] transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Slides */}
      {slides.map((s, idx) => {
        const imgSettings = s.coverImageSettings ?? {
          displayMode: "cover" as const,
          positionX: 50,
          positionY: 30,
          zoom: 1,
        };
        return (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${idx === current ? "opacity-100" : "opacity-0"}`}
          >
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={s.image}
                alt={s.title}
                className={`w-full h-full transition-transform duration-1000 ${imgSettings.displayMode === "contain" ? "object-contain" : "object-cover"}`}
                style={{
                  objectPosition: `${imgSettings.positionX ?? 50}% ${imgSettings.positionY ?? 30}%`,
                  transform: `scale(${imgSettings.zoom ?? 1})`,
                }}
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
          </div>
        );
      })}

      {/* Content */}
      <div className="absolute inset-0 flex items-end pb-20 z-20 w-full">
        <div className="max-w-screen-xl mx-auto px-6 md:px-10 w-full">
          <div className="max-w-3xl animate-fade-in-up" key={current}>
            <div className="flex items-center gap-3 mb-4">
              {slide.tag && (
                <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
                  {slide.tag === "featured" ? t("common.featured") : slide.tag}
                </span>
              )}
              <span className="bg-[#D4AF37] text-[#0A1628] text-xs font-bold px-3 py-1 rounded-full">
                {localizeCat(slide.categorySlug, slide.category)}
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 font-['Cairo']">
              {slide.title}
            </h2>
            <p className="text-white/75 text-base md:text-lg leading-relaxed mb-6 font-['Cairo'] max-w-2xl">
              {slide.description}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              {slide.slug ? (
                <button
                  onClick={() => navigate(`/article/${slide.slug}`)}
                  className="bg-white text-gray-900 font-semibold px-6 py-3 rounded-full hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap text-sm flex items-center gap-2"
                >
                  <i className="ri-book-open-line" />
                  {t("hero.readMore")}
                </button>
              ) : (
                <button
                  onClick={() => navigate("/category/destinations")}
                  className="bg-white text-gray-900 font-semibold px-6 py-3 rounded-full hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap text-sm flex items-center gap-2"
                >
                  <i className="ri-compass-3-line" />
                  {t("hero.exploreContent")}
                </button>
              )}
              <div className="flex items-center gap-3 text-white/60 text-sm">
                {flags?.showPublishDate !== false && (
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line" />
                    {formatArticleDate(slide.date, i18n.language)}
                  </span>
                )}
                {flags?.showAuthor !== false && (
                  <>
                    {flags?.showPublishDate !== false && <span className="w-1 h-1 rounded-full bg-white/40" />}
                    <span className="flex items-center gap-1.5">
                      <i className="ri-user-3-line" />
                      {slide.author}
                    </span>
                  </>
                )}
                {flags?.showReadTime !== false && (
                  <>
                    {(flags?.showPublishDate !== false || flags?.showAuthor !== false) && <span className="w-1 h-1 rounded-full bg-white/40" />}
                    <span className="flex items-center gap-1.5">
                      <i className="ri-time-line" />
                      {slide.readTime}
                    </span>
                  </>
                )}
                {flags?.showViews !== false && (
                  <>
                    {(flags?.showPublishDate !== false || flags?.showAuthor !== false || flags?.showReadTime !== false) && <span className="w-1 h-1 rounded-full bg-white/40" />}
                    <span className="flex items-center gap-1.5">
                      <i className="ri-eye-line" />
                      {slide.views}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide thumbnails */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => { setCurrent(idx); setProgress(0); }}
              className={`transition-all duration-300 cursor-pointer rounded-full ${
                idx === current ? "w-10 h-1.5 bg-[#D4AF37]" : "w-1.5 h-1.5 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      )}

      {/* Nav arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur-sm transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            onClick={next}
            className="absolute right-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur-sm transition-all cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </>
      )}

      {/* Breaking news ticker */}
      <div className="absolute bottom-0 right-0 left-0 z-20 bg-[#D4AF37]/95 backdrop-blur-sm">
        <div className="flex items-center h-10">
          <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded shrink-0 whitespace-nowrap mx-4">
            {t("nav.breaking")}
          </span>
          <span className="w-px h-5 bg-[#0A1628]/20 shrink-0" />
          <div className="flex-1 overflow-hidden flex items-center px-4">
            <button
              key={`${tickerIndex}-${tickerPhase}`}
              onClick={() => {
                const slug = activeItems[tickerIndex]?.slug;
                if (slug) navigate(`/article/${slug}`);
              }}
              className={`text-[#0A1628] text-sm font-semibold whitespace-nowrap leading-none text-right bg-transparent border-none p-0 ${
                activeItems[tickerIndex]?.slug
                  ? "cursor-pointer hover:underline underline-offset-2"
                  : "cursor-default"
              } ${
                tickerPhase === "in"
                  ? "ticker-fade-in"
                  : tickerPhase === "out"
                  ? "ticker-fade-out"
                  : ""
              }`}
            >
              {activeItems[tickerIndex]?.title ?? ""}
            </button>
          </div>
          <span className="text-[#0A1628]/50 text-xs font-medium shrink-0 mx-4 whitespace-nowrap">
            {tickerIndex + 1} / {activeItems.length}
          </span>
        </div>
      </div>
    </section>
  );
}
